<template>
  <header class="app-header">
    <div class="header-content">
      <div class="logo-container">
        <router-link to="/" class="logo">
          <span class="logo-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          </span>
          <span class="logo-text">首页</span>
        </router-link>
        <router-link to="/about" class="nav-link">
          <span class="nav-text">关于我们</span>
        </router-link>
      </div>

      <!-- 用户区域 -->
      <div class="user-area">
        <div v-if="isLoggedIn" class="user-logged-in">
          <div class="avatar-container" @click="toggleDropdown">
            <el-avatar :src="userAvatar" :size="32" class="avatar-img" />
            <div class="avatar-dropdown" :class="{ 'active': showDropdown }">
              <div class="dropdown-item" @click.stop="showPersonalCenter">
                <el-icon>
                  <User />
                </el-icon>
                <span>{{ userName }}</span>
              </div>
              <div class="dropdown-divider"></div>
              <!-- 消费记录按钮暂时隐藏 -->
              <!-- <div class="dropdown-item" @click="showTransactionHistory">
                <el-icon>
                  <CreditCard />
                </el-icon>
                <span>消费记录</span>
              </div>
              <div class="dropdown-divider"></div> -->
              <div class="dropdown-item" @click="handleLogout">
                <el-icon>
                  <SwitchButton />
                </el-icon>
                <span>退出登录</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="login-btn-container">
          <button class="login-button" @click="showLoginForm">登录</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { defineComponent, computed, ref, onMounted, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import { User, SwitchButton } from '@element-plus/icons-vue'
import defaultAvatar from '@/assets/notion-avatar-1746719215256.svg'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'AppHeader',
  components: {
    User,
    SwitchButton
  },
  setup() {
    const userStore = useUserStore()
    const router = useRouter()
    const isLoggedIn = computed(() => userStore.isLoggedIn)
    const userName = computed(() => userStore.username)
    const userAvatar = computed(() => userStore.userAvatar || defaultAvatar)
    const showDropdown = ref(false)

    const showLoginForm = () => {
      // 触发全局登录事件
      window.dispatchEvent(new CustomEvent('show-login-form'));
    };

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
      console.log('切换下拉菜单:', showDropdown.value);
    };

    const handleLogout = async () => {
      try {
        await userStore.logoutUser();
        showDropdown.value = false;
        console.log('退出登录成功');
        // 退出登录后导航到首页
        router.push('/');
      } catch (error) {
        console.error('退出登录失败:', error);
      }
    };

    const showTransactionHistory = () => {
      showDropdown.value = false;
      // 跳转到交易记录页面
      router.push('/account/transactions');
    };

    const showPersonalCenter = async () => {
      console.log('👤 点击跳转到个人中心');
      showDropdown.value = false;
      
      try {
        await router.push('/personal-center');
        console.log('✅ 跳转个人中心成功');
      } catch (error) {
        console.error('❌ 跳转个人中心失败:', error);
      }
    };

    // 点击其他地方关闭下拉菜单
    const closeDropdownOnOutsideClick = (event) => {
      if (showDropdown.value && !event.target.closest('.avatar-container')) {
        showDropdown.value = false;
      }
    };

    // 组件挂载后添加全局点击事件监听
    onMounted(() => {
      document.addEventListener('click', closeDropdownOnOutsideClick);
    });

    // 组件卸载前移除全局点击事件监听
    onUnmounted(() => {
      document.removeEventListener('click', closeDropdownOnOutsideClick);
    });

    return {
      isLoggedIn,
      userName,
      userAvatar,
      showDropdown,
      showLoginForm,
      toggleDropdown,
      handleLogout,
      showTransactionHistory,
      showPersonalCenter
    }
  }
})
</script>

<style scoped>
.app-header {
  background: rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  height: var(--header-height, 44px);
  width: 100%;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  height: 100%;
}

.logo-container {
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.logo-container::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  bottom: -2px;
  left: 0;
  background: linear-gradient(90deg, transparent, #3b82f6, transparent);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.logo {
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 18px;
  margin: 0;
  padding: 0 20px;
  font-weight: 500;
  text-decoration: none;
  position: relative;
  z-index: 1;
}

.logo-icon {
  display: flex;
  align-items: center;
  margin-right: 10px;
  color: #3b82f6;
  animation: pulse-glow 2s infinite ease-in-out;
  filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.4));
}

@keyframes pulse-glow {
  0% {
    opacity: 0.7;
    filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.2));
  }

  50% {
    opacity: 1;
    filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.5));
  }

  100% {
    opacity: 0.7;
    filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.2));
  }
}

.logo-text {
  background: linear-gradient(90deg, #fff, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.nav-link {
  display: flex;
  align-items: center;
  font-size: 13px;
  margin-left: 20px;
  padding: 5px 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  background: transparent;
}

.nav-link:hover {
  color: rgba(255, 255, 255, 0.95);
  background: rgba(255, 255, 255, 0.08);
}

.nav-link.router-link-active {
  font-weight: 500;
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.12);
}

.nav-text {
  font-weight: 500;
  letter-spacing: 0.5px;
  position: relative;
}

/* 用户区域样式 */
.user-area {
  display: flex;
  align-items: center;
  height: 100%;
  z-index: 10;
}

.user-logged-in {
  display: flex;
  align-items: center;
  gap: 10px;
}

.login-btn-container {
  display: flex;
  align-items: center;
  height: 100%;
  padding-right: 10px;
}

.avatar-container {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: visible;
  position: relative;
  cursor: pointer;
  border: 2px solid rgba(59, 130, 246, 0.5);
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-container:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
}

/* 移除hover触发下拉菜单，完全依赖点击事件 */

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

:deep(.el-avatar) {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid #639fff;
  box-shadow: 0 0 2px rgba(59, 130, 246, 0.5);
  background-color: #fff;
}

.avatar-dropdown {
  position: absolute;
  top: 38px;
  right: -74px;
  width: 180px;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s;
  z-index: 9999;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.avatar-dropdown::before {
  content: '';
  position: absolute;
  top: -8px;
  right: 84px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid rgba(15, 23, 42, 0.95);
  z-index: 1;
}

.dropdown-item {
  padding: 12px 15px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
}

.dropdown-item .el-icon {
  font-size: 16px;
  color: #3b82f6;
}

.dropdown-item:hover {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 5px 0;
}

.login-button {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  border: none;
  height: 32px;
  padding: 0 20px;
  border-radius: 16px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  white-space: nowrap;
  margin-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

@media (max-width: 768px) {
  .header-content {
    padding: 0 20px;
  }

  .logo {
    font-size: 16px;
    padding: 0 10px;
  }
}

.avatar-dropdown.active {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  display: block !important;
}
</style>
